import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, History, AlertCircle, CheckCircle, Building2, Truck, Home, MapPin, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getPresignedDownload, getDownloadUrlOrOpen } from '@/services/documentsService';

const PqrViewModal = ({ isOpen, onClose, pqr }) => {
  const [imageUrls, setImageUrls] = useState({});
  const [commentImageUrls, setCommentImageUrls] = useState({});

  useEffect(() => {
    if (isOpen && pqr) {
      loadImageUrls();
      loadCommentImageUrls();
    }
  }, [isOpen, pqr]);

  const loadImageUrls = async () => {
    if (!pqr?.adjuntos || pqr.adjuntos.length === 0) return;
    const urls = {};
    for (const adj of pqr.adjuntos) {
      try {
        if (adj.id) {
          const url = await getDownloadUrlOrOpen({ id: adj.id });
          urls[adj.id] = url;
        }
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
    setImageUrls(urls);
  };

  const loadCommentImageUrls = async () => {
    if (!pqr?.comentarios) return;
    const urls = {};
    for (const com of pqr.comentarios) {
      if (com.adjuntos && com.adjuntos.length > 0) {
        for (const adj of com.adjuntos) {
          try {
            if (adj.id) {
              const url = await getDownloadUrlOrOpen({ id: adj.id });
              urls[adj.id] = url;
            }
          } catch (error) {
            console.error('Error loading comment image:', error);
          }
        }
      }
    }
    setCommentImageUrls(urls);
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'Abierta': 'bg-red-100 text-red-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      'En espera': 'bg-yellow-100 text-yellow-800',
      'Cerrada': 'bg-green-100 text-green-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getPrioridadColor = (prioridad) => {
    const colors = {
      'Alta': 'bg-red-100 text-red-800',
      'Media': 'bg-yellow-100 text-yellow-800',
      'Baja': 'bg-green-100 text-green-800'
    };
    return colors[prioridad] || 'bg-gray-100 text-gray-800';
  };

  if (!pqr) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Detalles de PQR - {pqr.consecutivo}
            <Badge className="ml-2 bg-green-100 text-green-800">Cerrada</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-150px)] pr-4">
          <div className="space-y-4">
            {/* Información básica */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">{pqr.tipo}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Categoría</p>
                <p className="font-medium">{pqr.categoria}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estado</p>
                <Badge className={getEstadoColor(pqr.estado)}>{pqr.estado}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prioridad</p>
                <Badge className={getPrioridadColor(pqr.prioridad)}>{pqr.prioridad}</Badge>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">Descripción</p>
                <p className="text-sm">{pqr.descripcion}</p>
              </div>
            </div>

            {/* Imágenes adjuntas del PQR */}
            {pqr.adjuntos && pqr.adjuntos.length > 0 && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Imágenes Adjuntas ({pqr.adjuntos.length})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {pqr.adjuntos.map((adj, idx) => (
                    <div key={idx} className="relative group">
                      {imageUrls[adj.id] ? (
                        <img
                          src={imageUrls[adj.id]}
                          alt={adj.nombre || 'Adjunto'}
                          className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border"
                          onClick={() => window.open(imageUrls[adj.id], '_blank')}
                        />
                      ) : (
                        <div className="w-full h-32 bg-muted rounded-lg flex items-center justify-center border">
                          <ImageIcon className="h-8 w-8 text-muted-foreground animate-pulse" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Origen del PQR */}
            {pqr.origen && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  {pqr.origen.tipo === 'Lavandería' && <Building2 className="h-4 w-4" />}
                  {pqr.origen.tipo === 'Ruta / Conductor' && <Truck className="h-4 w-4" />}
                  {pqr.origen.tipo === 'Apartamento' && <Home className="h-4 w-4" />}
                  Origen del PQR
                </h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-purple-700">Área que genera el PQR</p>
                    <Badge className="mt-1">
                      {pqr.origen.tipo}
                    </Badge>
                  </div>
                  {pqr.origen.tipo === 'Apartamento' && pqr.origen.propiedad && (
                    <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-purple-700">Propiedad</p>
                          <p className="text-sm font-medium">{pqr.origen.propiedad.nombre}</p>
                        </div>
                        {pqr.origen.propiedad.edificio && (
                          <div>
                            <p className="text-xs text-purple-700">Edificio</p>
                            <p className="text-sm">
                              {pqr.origen.propiedad.edificio.nombre}
                              {pqr.origen.propiedad.edificio.ciudad && ` • ${pqr.origen.propiedad.edificio.ciudad}`}
                            </p>
                          </div>
                        )}
                        {pqr.origen.apartamento && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3 w-3 text-purple-600" />
                            <div>
                              <p className="text-xs text-purple-700">Apartamento</p>
                              <p className="text-sm font-medium">{pqr.origen.apartamento}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Información del reportante */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Información del Reportante</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-sm text-blue-700">Nombre</p>
                  <p className="text-sm font-medium">{pqr.usuarioReporta?.nombre}</p>
                </div>
                {pqr.usuarioReporta?.correo && (
                  <div>
                    <p className="text-sm text-blue-700">Correo</p>
                    <p className="text-sm">{pqr.usuarioReporta.correo}</p>
                  </div>
                )}
                {pqr.usuarioReporta?.telefono && (
                  <div>
                    <p className="text-sm text-blue-700">Teléfono</p>
                    <p className="text-sm">{pqr.usuarioReporta.telefono}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-blue-700">Fecha de registro</p>
                  <p className="text-sm">
                    {format(new Date(pqr.fechaRegistro), 'dd/MM/yyyy HH:mm', { locale: es })}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Solución aplicada */}
            {pqr.solucion && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Solución Aplicada
                </h3>
                <p className="text-sm text-green-800 whitespace-pre-wrap">{pqr.solucion}</p>
                <div className="mt-3 pt-3 border-t border-green-200 grid grid-cols-2 gap-3">
                  {pqr.fechaCierre && (
                    <div>
                      <p className="text-xs text-green-700">Fecha de cierre</p>
                      <p className="text-sm font-medium">
                        {format(new Date(pqr.fechaCierre), 'dd/MM/yyyy HH:mm', { locale: es })}
                      </p>
                    </div>
                  )}
                  {pqr.usuarioCierre && (
                    <div>
                      <p className="text-xs text-green-700">Cerrado por</p>
                      <p className="text-sm font-medium">{pqr.usuarioCierre.nombre}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Historial de comentarios */}
            {pqr.comentarios && pqr.comentarios.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  <h3 className="font-semibold">Historial de Comentarios</h3>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {pqr.comentarios.map((com, index) => (
                    <div key={index} className="p-3 bg-muted/30 rounded-lg text-sm">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium">{com.usuario?.nombre || 'Usuario'}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(com.fecha), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </span>
                      </div>
                      <p className="text-muted-foreground">{com.comentario}</p>
                      {com.accion && (
                        <p className="text-xs text-blue-600 mt-1">Acción: {com.accion}</p>
                      )}
                      {com.adjuntos && com.adjuntos.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {com.adjuntos.map((adj, adjIdx) => (
                            <div key={adjIdx} className="relative group">
                              {commentImageUrls[adj.id] ? (
                                <img
                                  src={commentImageUrls[adj.id]}
                                  alt={adj.nombre || 'Adjunto'}
                                  className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity border"
                                  onClick={() => window.open(commentImageUrls[adj.id], '_blank')}
                                />
                              ) : (
                                <div className="w-full h-24 bg-muted rounded flex items-center justify-center border">
                                  <ImageIcon className="h-6 w-6 text-muted-foreground animate-pulse" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historial de cambios */}
            {pqr.historialCambios && pqr.historialCambios.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  <h3 className="font-semibold">Historial de Cambios de Estado</h3>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {pqr.historialCambios.map((cambio, index) => (
                    <div key={index} className="p-2 bg-muted/20 rounded text-xs">
                      <div className="flex justify-between items-start">
                        <span className="font-medium">{cambio.usuario?.nombre || 'Usuario'}</span>
                        <span className="text-muted-foreground">
                          {format(new Date(cambio.fecha), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-1">
                        Cambió de <Badge className="text-xs">{cambio.estadoAnterior}</Badge> a{' '}
                        <Badge className="text-xs">{cambio.estadoNuevo}</Badge>
                      </p>
                      {cambio.comentario && <p className="mt-1">{cambio.comentario}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Responsable asignado */}
            {pqr.responsable && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Responsable asignado</p>
                <p className="font-medium">{pqr.responsable.nombre}</p>
                {pqr.responsable.correo && (
                  <p className="text-sm text-muted-foreground">{pqr.responsable.correo}</p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PqrViewModal;
